/**
 * Inventory domain API functions (Step 7.1.6).
 *
 * Inventory items CRUD, assignments, damage reports, inspections.
 * Extracted from adminData/index.js for team scalability.
 */
import {
  API_BASE_URL,
  getApiHeaders,
  getApiHeadersMultipart,
  parseAdminErrorResponse,
} from "./core";

/** GET /api/admin/inventory/{itemId} — single item with assigned/available */
export function fetchAdminInventoryItem(itemId) {
  return fetch(
    `${API_BASE_URL}/api/admin/inventory/${encodeURIComponent(itemId)}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) {
      if (res.status === 404) return null;
      throw new Error(await parseAdminErrorResponse(res));
    }
    return res.json();
  });
}

/** GET /api/admin/inventory — catalog with assigned/available per item */
export function fetchAdminInventory(params = {}) {
  const sp = new URLSearchParams();
  if (params.skip != null) sp.set("skip", String(params.skip));
  if (params.limit != null) sp.set("limit", String(params.limit));
  if (params.q) sp.set("q", params.q);
  if (params.category) sp.set("category", params.category);
  if (params.status) sp.set("status", params.status);
  if (params.unit_id) sp.set("unit_id", params.unit_id);
  if (params.has_open_damage === true) sp.set("has_open_damage", "true");
  const qs = sp.toString();
  return fetch(`${API_BASE_URL}/api/admin/inventory${qs ? `?${qs}` : ""}`, {
    headers: getApiHeaders(),
  }).then((res) => {
    if (!res.ok) throw new Error("Inventar konnte nicht geladen werden.");
    return res.json();
  });
}

/** GET /api/admin/inventory/summary */
export function fetchAdminInventorySummary() {
  return fetch(`${API_BASE_URL}/api/admin/inventory/summary`, {
    headers: getApiHeaders(),
  }).then((res) => {
    if (!res.ok) throw new Error("Inventar-Zusammenfassung konnte nicht geladen werden.");
    return res.json();
  });
}

export async function createInventoryItem(body) {
  const res = await fetch(`${API_BASE_URL}/api/admin/inventory`, {
    method: "POST",
    headers: getApiHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

/** POST /api/admin/inventory/import-preview — Smart Import stub (deterministic, no persistence). */
export async function postInventoryImportPreview(body) {
  const res = await fetch(`${API_BASE_URL}/api/admin/inventory/import-preview`, {
    method: "POST",
    headers: getApiHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

export async function updateInventoryItem(itemId, body) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/inventory/${encodeURIComponent(itemId)}`,
    {
      method: "PATCH",
      headers: getApiHeaders(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

export async function deleteInventoryItem(itemId) {
  const res = await fetch(`${API_BASE_URL}/api/admin/inventory/${encodeURIComponent(itemId)}`, {
    method: "DELETE",
    headers: getApiHeaders(),
  });
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  try {
    return await res.json();
  } catch {
    return { status: "ok" };
  }
}

/** GET /api/admin/units/{unitId}/inventory-assignments */
export function fetchUnitInventoryAssignments(unitId) {
  return fetch(
    `${API_BASE_URL}/api/admin/units/${encodeURIComponent(unitId)}/inventory-assignments`,
    { headers: getApiHeaders() }
  ).then((res) => {
    if (!res.ok) throw new Error("Zuordnungen konnten nicht geladen werden.");
    return res.json();
  });
}

export function fetchInventoryItemAssignments(itemId) {
  return fetch(
    `${API_BASE_URL}/api/admin/inventory/${encodeURIComponent(itemId)}/assignments`,
    { headers: getApiHeaders() }
  ).then((res) => {
    if (!res.ok) throw new Error("Zuordnungen konnten nicht geladen werden.");
    return res.json();
  });
}

export async function createInventoryAssignment(itemId, body) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/inventory/${encodeURIComponent(itemId)}/assignments`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

export async function updateInventoryAssignment(assignmentId, body) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/inventory/assignments/${encodeURIComponent(assignmentId)}`,
    {
      method: "PATCH",
      headers: getApiHeaders(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

export async function deleteInventoryAssignment(assignmentId, opts = {}) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/inventory/assignments/${encodeURIComponent(assignmentId)}`,
    {
      method: "DELETE",
      headers: getApiHeaders(),
      body: JSON.stringify({ reason: opts.reason ?? null }),
    }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  try {
    return await res.json();
  } catch {
    return { status: "ok" };
  }
}

/** POST /api/admin/inventory/assignments/{assignmentId}/move — end old + create new */
export async function moveInventoryAssignment(assignmentId, { unit_id, room_id, reason }) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/inventory/assignments/${encodeURIComponent(assignmentId)}/move`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify({ unit_id, room_id: room_id || null, reason: reason || null }),
    }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

/** POST /api/admin/inventory/damage-reports */
export async function createDamageReport(body) {
  const res = await fetch(`${API_BASE_URL}/api/admin/inventory/damage-reports`, {
    method: "POST",
    headers: getApiHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

/**
 * GET /api/admin/inventory/damage-reports
 * @param {{ assignment_id?: string, tenancy_id?: string, damage_status?: string }} [params]
 */
export function fetchDamageReports(params = {}) {
  const sp = new URLSearchParams();
  if (params.assignment_id) sp.set("assignment_id", params.assignment_id);
  if (params.tenancy_id) sp.set("tenancy_id", params.tenancy_id);
  if (params.damage_status) sp.set("damage_status", params.damage_status);
  const qs = sp.toString();
  return fetch(
    `${API_BASE_URL}/api/admin/inventory/damage-reports${qs ? `?${qs}` : ""}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

/** GET /api/admin/inventory/damage-reports/{reportId} */
export function fetchDamageReport(reportId) {
  return fetch(
    `${API_BASE_URL}/api/admin/inventory/damage-reports/${encodeURIComponent(reportId)}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

/** PATCH /api/admin/inventory/damage-reports/{reportId} */
export async function patchDamageReport(reportId, body) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/inventory/damage-reports/${encodeURIComponent(reportId)}`,
    {
      method: "PATCH",
      headers: getApiHeaders(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

/** POST /api/admin/inventory/inspections */
export async function createInspection(body) {
  const res = await fetch(`${API_BASE_URL}/api/admin/inventory/inspections`, {
    method: "POST",
    headers: getApiHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

/**
 * GET /api/admin/inventory/inspections
 * @param {{ tenancy_id?: string, unit_id?: string, type?: string }} [params]
 */
export function fetchInspections(params = {}) {
  const sp = new URLSearchParams();
  if (params.tenancy_id) sp.set("tenancy_id", params.tenancy_id);
  if (params.unit_id) sp.set("unit_id", params.unit_id);
  if (params.type) sp.set("type", params.type);
  const qs = sp.toString();
  return fetch(
    `${API_BASE_URL}/api/admin/inventory/inspections${qs ? `?${qs}` : ""}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

/** GET /api/admin/inventory/inspections/{inspectionId} */
export function fetchInspection(inspectionId) {
  return fetch(
    `${API_BASE_URL}/api/admin/inventory/inspections/${encodeURIComponent(inspectionId)}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

/** PATCH /api/admin/inventory/inspections/{inspectionId} */
export async function patchInspection(inspectionId, body) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/inventory/inspections/${encodeURIComponent(inspectionId)}`,
    {
      method: "PATCH",
      headers: getApiHeaders(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

/**
 * POST /api/admin/inventory/inspections/{inspectionId}/finalize
 * Finalize (lock) a handover inspection. After this, content mutations 409.
 */
export async function finalizeInspection(inspectionId, finalizedNote) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/inventory/inspections/${encodeURIComponent(inspectionId)}/finalize`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify(
        finalizedNote != null ? { finalized_note: finalizedNote } : {}
      ),
    }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

/**
 * POST /api/admin/inventory/inspections/{id}/protocol-pdf/generate[?force=true]
 * Generate/regenerate the handover protocol PDF (finalized inspections only).
 */
export async function generateInspectionProtocolPdf(
  inspectionId,
  { force = false } = {}
) {
  const qs = force ? "?force=true" : "";
  const res = await fetch(
    `${API_BASE_URL}/api/admin/inventory/inspections/${encodeURIComponent(inspectionId)}/protocol-pdf/generate${qs}`,
    { method: "POST", headers: getApiHeaders() }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

/** GET /api/admin/inventory/inspections/{id}/protocol-pdf/url → { url } */
export async function fetchInspectionProtocolPdfUrl(inspectionId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/inventory/inspections/${encodeURIComponent(inspectionId)}/protocol-pdf/url`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  const data = await res.json();
  return data?.url || "";
}

/**
 * POST /api/admin/inventory/inspections/{id}/signature/sign-dual → signed metadata
 * (ÜP-S3). Dual-sign: tenant + management/landlord representative both sign in the
 * same session. signature_place travels in this payload (NOT via patchInspection,
 * which the backend locks after finalize).
 */
export async function signInspectionDual(
  inspectionId,
  {
    signature_place,
    signed_by_name,
    signature_data_url,
    management_signed_by_name,
    management_signature_data_url,
  } = {}
) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/inventory/inspections/${encodeURIComponent(inspectionId)}/signature/sign-dual`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify({
        signature_place,
        signed_by_name,
        signature_data_url,
        management_signed_by_name,
        management_signature_data_url,
      }),
    }
  );
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    // Neutral wording on purpose: this helper does not know the inspection
    // type, and threading it through just for a fallback string is not worth
    // an API signature change. The typed message comes from the backend detail.
    throw new Error(detail || "Protokoll konnte nicht unterzeichnet werden.");
  }
  return res.json();
}

/** GET /api/admin/inventory/inspections/{id}/signed-pdf/url → URL string ("" if none) */
export async function fetchInspectionSignedPdfUrl(inspectionId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/inventory/inspections/${encodeURIComponent(inspectionId)}/signed-pdf/url`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "Unterschriebenes PDF konnte nicht geöffnet werden.");
  }
  const data = await res.json();
  return data?.url || "";
}

/* ─────────────────────────────────────────────────────────────
 * Inventory + damage photos (Sprint 1 — manual upload)
 * ─────────────────────────────────────────────────────────────
 */

/** GET /api/admin/inventory/{itemId}/photos */
export function fetchInventoryPhotos(itemId) {
  return fetch(
    `${API_BASE_URL}/api/admin/inventory/${encodeURIComponent(itemId)}/photos`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

/**
 * POST /api/admin/inventory/{itemId}/photos
 * Multipart upload: 1..5 files, optional caption applied to every uploaded photo.
 */
export async function uploadInventoryPhotos(itemId, files, options = {}) {
  const list = Array.from(files || []);
  if (list.length === 0) throw new Error("Keine Datei ausgewählt.");
  const fd = new FormData();
  for (const f of list) fd.append("files", f, f.name);
  const cap = options.caption != null ? String(options.caption).trim() : "";
  if (cap) fd.append("caption", cap);
  const res = await fetch(
    `${API_BASE_URL}/api/admin/inventory/${encodeURIComponent(itemId)}/photos`,
    { method: "POST", headers: getApiHeadersMultipart(), body: fd }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

/** PATCH /api/admin/inventory-photos/{photoId} */
export async function patchInventoryPhoto(photoId, body) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/inventory-photos/${encodeURIComponent(photoId)}`,
    {
      method: "PATCH",
      headers: getApiHeaders(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

/** DELETE /api/admin/inventory-photos/{photoId} */
export async function deleteInventoryPhoto(photoId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/inventory-photos/${encodeURIComponent(photoId)}`,
    { method: "DELETE", headers: getApiHeaders() }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  try {
    return await res.json();
  } catch {
    return { ok: true };
  }
}

/** GET /api/admin/damage-reports/{damageId}/photos */
export function fetchDamagePhotos(damageId) {
  return fetch(
    `${API_BASE_URL}/api/admin/damage-reports/${encodeURIComponent(damageId)}/photos`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

/** POST /api/admin/damage-reports/{damageId}/photos */
export async function uploadDamagePhotos(damageId, files, options = {}) {
  const list = Array.from(files || []);
  if (list.length === 0) throw new Error("Keine Datei ausgewählt.");
  const fd = new FormData();
  for (const f of list) fd.append("files", f, f.name);
  const cap = options.caption != null ? String(options.caption).trim() : "";
  if (cap) fd.append("caption", cap);
  const res = await fetch(
    `${API_BASE_URL}/api/admin/damage-reports/${encodeURIComponent(damageId)}/photos`,
    { method: "POST", headers: getApiHeadersMultipart(), body: fd }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

/** PATCH /api/admin/damage-photos/{photoId} */
export async function patchDamagePhoto(photoId, body) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/damage-photos/${encodeURIComponent(photoId)}`,
    {
      method: "PATCH",
      headers: getApiHeaders(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

/** DELETE /api/admin/damage-photos/{photoId} */
export async function deleteDamagePhoto(photoId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/damage-photos/${encodeURIComponent(photoId)}`,
    { method: "DELETE", headers: getApiHeaders() }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  try {
    return await res.json();
  } catch {
    return { ok: true };
  }
}

/** PATCH /api/admin/inventory/inspections/{inspectionId}/items/{itemId} */
export async function patchInspectionItem(inspectionId, itemId, body) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/inventory/inspections/${encodeURIComponent(inspectionId)}/items/${encodeURIComponent(itemId)}`,
    {
      method: "PATCH",
      headers: getApiHeaders(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

/**
 * POST /api/admin/inventory/inspections/{inspectionId}/items/{itemId}/photos
 * Multipart upload of 1..N handover photos for one checklist row.
 */
export async function uploadInspectionItemPhotos(inspectionId, itemId, files) {
  const list = Array.from(files || []);
  if (list.length === 0) throw new Error("Keine Datei ausgewählt.");
  const fd = new FormData();
  for (const f of list) fd.append("files", f, f.name);
  const res = await fetch(
    `${API_BASE_URL}/api/admin/inventory/inspections/${encodeURIComponent(inspectionId)}/items/${encodeURIComponent(itemId)}/photos`,
    { method: "POST", headers: getApiHeadersMultipart(), body: fd }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

/** DELETE /api/admin/inspection-item-photos/{photoId} */
export async function deleteInspectionItemPhoto(photoId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/inspection-item-photos/${encodeURIComponent(photoId)}`,
    { method: "DELETE", headers: getApiHeaders() }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  try {
    return await res.json();
  } catch {
    return { ok: true };
  }
}
